// pages/sections/ClientDataSection.js
// Client Data — review, edit and sign off each client's PE engagement attributes
//   Is Paper · Orphan (Standalone) · DNO (Do Not Offshore) · FAM/EMP/Referral · Post 4/15
// plus its Client Manager + Partner.
//
// Scope: you see the clients where you are the Client Manager or Partner; an
// admin sees every client. A client's Manager/Partner (or an admin) edits right in
// the row (or many at once via Bulk edit), then each role signs off — Manager first;
// a Partner signing first pulls the Manager's sign-off too (pull-to-self). A client
// can also be marked No Longer Client from its row. DI never writes Practice Engine,
// so edits are recorded here and flagged "pending PE" (the dot beside an edited
// value). Also shown, read-only: last-12-months billing and the Signer/Relationship
// Group. Backed by /api/diClientData/{period}. The summary cards double as property
// filters (click to toggle; active filters AND together).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../../auth/AuthContext';
import { usePeriod } from '../../hooks/usePeriod';
import { useSectionData } from '../../hooks/useSectionData';
import { useRowMutations } from '../../hooks/useRowMutations';
import LastRefreshed from '../../components/LastRefreshed';
import ToggleSwitch from './ToggleSwitch';
import ClientDataBulkModal from './ClientDataBulkModal';
import RoleToggle from './RoleToggle';
import ManagerUnsignedWarningModal from '../../components/ManagerUnsignedWarningModal';
import ClientBillingModal from './ClientBillingModal';
import ClientGroupModal from './ClientGroupModal';
import DisengagementModal from './DisengagementModal';
import NlcRequestedBadge from './NlcRequestedBadge';
import {
  nlcModalProps, announceNlcOutcome, nlcLeavesList, requestFlagFrom, withdrawNlcRequest, sweepRelated,
} from './nlcRequestUi';
import { FLAGS, formatMoney } from './clientDataUtil';
import { postAdminUnmark, sortByTier } from './reviewUtil';
import './ReviewTable.css';
import './ClientDataSection.css';

const PAGE_SIZE = 50;

// Property filters (the five requested attributes). `test` is the row predicate;
// (The card counts are derived from the rows on the client, so they move with an optimistic edit.)
const PROPS = [
  { key: 'paper',  label: 'Is Paper',              summaryKey: 'isPaper',        test: r => r.isPaper },
  { key: 'orphan', label: 'Orphan (Standalone)',   summaryKey: 'isOrphan',       test: r => r.isOrphan },
  { key: 'dno',    label: 'DNO — Do Not Offshore', summaryKey: 'doNotOffshore',  test: r => r.doNotOffshore },
  { key: 'fer',    label: 'FAM / EMP / Referral',  summaryKey: 'famEmpReferral', test: r => r.isFam || r.isEmp || r.isReferral },
  { key: 'p415',   label: 'Post 4/15',             summaryKey: 'postApril15',    test: r => r.postApril15 },
  { key: 'ownedByPe', label: 'Owned by PE',        summaryKey: 'ownedByPe',      test: r => r.ownedByPe },
];

// Properties kept in Data Integrity only (PE has no field): saved, but never flagged "pending PE".
const DI_ONLY = new Set(FLAGS.filter(f => f.diOnly).map(f => f.prop));

const FER_TYPES = [
  { value: '',         label: 'FAM / EMP / Referral: any' },
  { value: 'fam',      label: 'FAM only' },
  { value: 'emp',      label: 'EMP only' },
  { value: 'referral', label: 'Referral source only' },
];

const STATUS_OPTIONS = [
  { value: '',           label: 'All statuses' },
  { value: 'needs',      label: 'Needs sign-off' },
  { value: 'signed',     label: 'Signed off' },
  { value: 'pending-pe', label: 'Edited — pending PE' },
];
// Partner-only: clients the Manager has signed that are still waiting on the viewer as Partner.
const AWAITING_PARTNER = 'awaiting-partner';
const awaitsMyPartnerSignoff = r => r.myRoles.includes('partner') && !!r.reviews.manager && !r.reviews.partner;

// What the Group cell shows: the Signer Group when it has other members; null = no signer group
// (nothing to show). The Relationship Group is still listed in the member modal.
function groupDisplay(g) {
  if (!g) return null;
  if (g.sgSize > 1) return { kind: 'SG', name: g.sgName, size: g.sgSize };
  return null;
}

// Error toasts stay long enough to read the server's reason.
const ERR_TOAST = { autoClose: 10000 };

// A firm-wide NLC count with `n` added (the footer line); `summary` may be absent.
const bumpNlc = (summary, n) => (summary ? { ...summary, nlcExcluded: Math.max(0, (summary.nlcExcluded || 0) + n) } : summary);

// What a change-set shows on one row before the server answers (the same patches the inline
// controls apply), plus the properties that get their "pending PE" dot. `canStaff`: the viewer may
// reassign this client's Manager / Partner (its Partner, or an admin) — otherwise the server skips those.
function optimisticFor(changes, canStaff, employees) {
  const nameOf = em => (employees.find(e => e.email === em) || {}).name || em;
  const patch = {};
  const dots = [];
  for (const [k, v] of Object.entries(changes)) {
    if (k === 'famEmp') {
      patch.isFam = v.type === 'fam';
      patch.isEmp = v.type === 'emp';
      patch.famEmpValue = v.type === 'fam' ? 'Friend and Family' : (v.type === 'emp' ? nameOf(v.employeeEmail) : '');
      dots.push('famEmp');
    } else if (k === 'managerEmail' || k === 'partnerEmail') {
      if (!canStaff) continue;
      const who = k === 'managerEmail' ? 'manager' : 'partner';
      patch[k] = v;
      patch[who] = nameOf(v);
      dots.push(who);
    } else {
      patch[k] = v;
      if (!DI_ONLY.has(k)) dots.push(k);
    }
  }
  return { patch, dots };
}

// The row's `pending` list after an edit, decided the way the server will decide it: an edited property
// keeps (or gains) its "pending PE" dot only while it DIFFERS from what PE holds; setting it back to PE's
// value drops the dot at once (the server deletes the override). `patch` is what the edit shows on the row
// (the same fields the inline controls patch), `dots` the property names it touches. PE's value for a
// property that's already pending is `row.pe[prop]`; for one that isn't, PE's value IS the row's.
function nextPending(row, patch, dots) {
  const lc = v => String(v == null ? '' : v).trim().toLowerCase();
  const out = new Set(row.pending || []);
  for (const prop of dots) {
    const was = out.has(prop);
    let same;
    if (prop === 'famEmp') {
      same = lc(was ? (row.pe && row.pe.famEmp) : row.famEmpValue) === lc(patch.famEmpValue);
    } else if (prop === 'manager' || prop === 'partner') {
      const emailKey = `${prop}Email`;
      same = was ? (!!(row.pe && row.pe[prop]) && lc(row.pe[prop]) === lc(patch[prop])) : lc(row[emailKey]) === lc(patch[emailKey]);
    } else {
      same = !!(was ? (row.pe && row.pe[prop]) : row[prop]) === !!patch[prop];
    }
    if (same) out.delete(prop); else out.add(prop);
  }
  return [...out];
}

const peText = v => (typeof v === 'boolean' ? (v ? 'Yes' : 'No') : (v || 'none'));

// A "pending PE" dot beside an edited value, with what PE currently says.
function Pend({ row, prop }) {
  if (!row.pending || !row.pending.includes(prop)) return null;
  return <span className="cd-pend" title={`Edited in Data Integrity — PE currently: ${peText(row.pe && row.pe[prop])}`}>●</span>;
}

// Does this row still need a sign-off from the viewer? (their roles; admin: either)
// With the Manager / Partner toggle on, a client needs sign-off when the viewed role hasn't signed it.
const needsSignoff = (r, viewRole) => (viewRole ? !r.reviews[viewRole] : (r.myRoles.length ? r.myRoles : ['manager', 'partner']).some(role => !r.reviews[role]));

const famEmpType = r => (r.isFam ? 'fam' : (r.isEmp ? 'emp' : 'none'));

// Make sure the current value is selectable even if the roster lacks it.
function withCurrent(employees, email, name) {
  if (!email || employees.some(e => e.email === email)) return employees;
  return [{ email, name: name || email }, ...employees];
}

// The four on/off properties share one column. Each is its own checkbox, saved
// the moment it's toggled. Read-only, only the ones that are set show (as chips).
function FlagsCell({ row, editable, onToggle }) {
  if (!editable) {
    const set = FLAGS.filter(f => row[f.prop]);
    return set.length ? (
      <span className="cd-tags">
        {set.map(f => <span key={f.prop} className="cd-tag cd-tag--flag" title={f.help || f.title}>{f.label}<Pend row={row} prop={f.prop} /></span>)}
      </span>
    ) : <span className="rt-cell-muted">—</span>;
  }
  return (
    <div className="cd-flags">
      {FLAGS.map(f => (
        <label key={f.prop} className="cd-flag" title={f.help || f.title}>
          <input type="checkbox" className="cd-inline" checked={!!row[f.prop]}
                 onChange={e => onToggle(f.prop, e.target.checked)} />
          {f.label}<Pend row={row} prop={f.prop} />
        </label>
      ))}
    </div>
  );
}

// Inline FAM / EMP (+ employee) and Referral. Choosing "Employee" waits for the
// employee to be picked before anything is saved.
function FerCell({ row, employees, editable, onSave }) {
  const current = famEmpType(row);
  const [type, setType] = useState(current);
  useEffect(() => { setType(current); }, [current, row.famEmpValue]);
  const empEmail = useMemo(() => {
    const m = employees.find(e => e.name.toLowerCase() === String(row.famEmpValue || '').toLowerCase());
    return m ? m.email : '';
  }, [employees, row.famEmpValue]);

  if (!editable) {
    return (
      <>
        {(row.isFam || row.isEmp || row.isReferral) ? (
          <span className="cd-tags">
            {row.isFam      && <span className="cd-tag cd-tag--fam">FAM</span>}
            {row.isEmp      && <span className="cd-tag cd-tag--emp" title={row.famEmpValue ? `Employee: ${row.famEmpValue}` : undefined}>EMP</span>}
            {row.isReferral && <span className="cd-tag cd-tag--ref">Referral</span>}
          </span>
        ) : <span className="rt-cell-muted">—</span>}
        <Pend row={row} prop="famEmp" /><Pend row={row} prop="isReferral" />
      </>
    );
  }

  const pickType = v => {
    setType(v);
    if (v !== 'emp' && v !== current) onSave({ famEmp: { type: v } }, { isFam: v === 'fam', isEmp: false, famEmpValue: v === 'fam' ? 'Friend and Family' : '' });
  };
  const pickEmployee = email => {
    if (!email) return;
    const name = (employees.find(e => e.email === email) || {}).name || '';
    onSave({ famEmp: { type: 'emp', employeeEmail: email } }, { isFam: false, isEmp: true, famEmpValue: name });
  };

  return (
    <div className="cd-fe">
      <span>
        <select className="cd-cell-select" value={type} aria-label="FAM / EMP" onChange={e => pickType(e.target.value)}>
          <option value="none">—</option>
          <option value="fam">Friend &amp; Family</option>
          <option value="emp">Employee</option>
        </select>
        <Pend row={row} prop="famEmp" />
      </span>
      {type === 'emp' && (
        <select className="cd-cell-select" value={current === 'emp' ? empEmail : ''} aria-label="Employee"
                onChange={e => pickEmployee(e.target.value)}>
          <option value="">{current === 'emp' && row.famEmpValue ? row.famEmpValue : '— Select employee —'}</option>
          {employees.map(e => <option key={e.email} value={e.email}>{e.name}</option>)}
        </select>
      )}
      <label className="cd-fe-ref">
        <input type="checkbox" className="cd-inline" checked={!!row.isReferral}
               onChange={e => onSave({ isReferral: e.target.checked }, { isReferral: e.target.checked })} />
        Referral<Pend row={row} prop="isReferral" />
      </label>
    </div>
  );
}

export default function ClientDataSection() {
  const { email, isAdmin } = useAuth();
  const { period, periodActive } = usePeriod();

  const [search, setSearch]   = useState('');
  const [office, setOffice]   = useState('');
  const [partner, setPartner] = useState('');
  const [manager, setManager] = useState('');
  const [ferType, setFerType] = useState('');
  const [status, setStatus]   = useState('');
  const [active, setActive]   = useState(() => new Set());
  const [page, setPage]       = useState(1);
  const [bulkMode, setBulkMode] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [mgrWarn, setMgrWarn]   = useState(null);          // { unsigned, total } — Partner bulk-signing clients the Manager hasn't
  const [skipped, setSkipped]   = useState(null);          // last bulk run's refusals
  const [billingRow, setBillingRow] = useState(null);      // client whose 12-month billing is open
  const [groupRow, setGroupRow] = useState(null);          // client whose Signer/Relationship group is open
  const [groupMode, setGroupMode] = useState('');          // '' | 'grouped' | 'standalone'
  const [groupOnly, setGroupOnly] = useState(null);        // { code, label, kind } — list limited to one group
  const [nlcRow, setNlcRow]     = useState(null);          // client pending the NLC disengagement form
  const [nlcSaving, setNlcSaving] = useState(false);
  const [nlcError, setNlcError]   = useState(null);          // last failed NLC submit — shown in the modal

  const fetcher = useCallback(async () => {
    if (!period) return null;
    const res = await fetch(`/api/diClientData/${period}`, { credentials: 'include' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }, [period]);

  // Optimistic saves: ordered per client, never disable the controls, and a refetch can't overwrite an edit.
  const mutations = useRowMutations(r => r.contIndex);
  const { pinned, clearPinned } = mutations;

  const { data, setData, initialLoading, error, lastRefreshedAt, freshness, silentRefetch } =
    useSectionData(fetcher, {
      deps: [period, email],
      heartbeat: { sections: ['client-data'], period },
      reconcile: mutations.reconcile,
    });

  // Rows edited this session stay listed even if the edit stops them matching the filters,
  // until the user changes a filter.
  useEffect(() => { clearPinned(); }, [search, office, partner, manager, ferType, status, active, groupMode, groupOnly, clearPinned]);

  const allRows = useMemo(() => (data && data.rows) || [], [data]);
  const employees = useMemo(() => (data && data.employees) || [], [data]);
  const adminView = !!(data && data.adminView);
  // Manager / Partner toggle (non-admin only — the admin view is firm-wide, with no role). Until one is
  // picked, default to a role the user actually has: Partner only when they're ONLY a partner.
  const [roleChoice, setRoleChoice] = useState(null);
  const hasManagerRows = allRows.some(r => r.myRoles.includes('manager'));
  const hasPartnerRows = allRows.some(r => r.myRoles.includes('partner'));
  const activeRole = adminView ? null : (roleChoice || (hasPartnerRows && !hasManagerRows ? 'partner' : 'manager'));
  const rows = useMemo(() => (activeRole ? allRows.filter(r => r.myRoles.includes(activeRole)) : allRows), [allRows, activeRole]);
  const readOnly = !periodActive;

  const patchRow = useCallback((contIndex, updater) => {
    let prev = null;
    setData(d => {
      if (!d) return d;
      return { ...d, rows: d.rows.map(r => {
        if (String(r.contIndex) !== String(contIndex)) return r;
        prev = r;
        return updater(r);
      }) };
    });
    return prev;
  }, [setData]);

  // ── sign-off ───────────────────────────────────────────────────────────
  // Sign-offs and edits for one client share a queue (keyed by contIndex), so they reach the server in click order.
  async function signOff(row, role) {
    const stamp = { by: 'You', at: new Date().toISOString() };
    patchRow(row.contIndex, r => ({
      ...r,
      reviews: { ...r.reviews, [role]: stamp, ...(role === 'partner' && !r.reviews.manager ? { manager: stamp } : {}) },
    }));
    try {
      await mutations.run(row.contIndex, async () => {
        const res = await fetch(`/api/diClientData/${period}`, {
          method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'sign-off', contIndex: row.contIndex, role }),
        });
        const j = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(j.error || `HTTP ${res.status}`);
      }, { revert: () => patchRow(row.contIndex, () => row), refetch: silentRefetch });
      toast.success(`Signed off (${role}).`);
    } catch (err) {
      toast.error(`Couldn't sign off: ${err.message}`, ERR_TOAST);
    }
  }

  // The manager takes their sign-off back (an admin uses adminUnmark instead). The server only allows it while
  // the partner hasn't signed off or saved a change on the client; one person holding both roles loses both.
  const holdsBoth = r => !!r.managerEmail && r.managerEmail.toLowerCase() === (r.partnerEmail || '').toLowerCase();
  const canRevoke = (r, role) => !readOnly && role === 'manager' && r.myRoles.includes('manager') && !!r.reviews.manager
    && (holdsBoth(r) || (!r.reviews.partner && !r.partnerEdited));

  async function revokeSignOff(row) {
    if (!window.confirm(`Take back your sign-off for ${row.clientCode}? You'll be able to edit it again and will need to sign off again when you're done.`)) return;
    patchRow(row.contIndex, r => ({ ...r, reviews: { ...r.reviews, manager: null, ...(holdsBoth(r) ? { partner: null } : {}) } }));
    try {
      await mutations.run(row.contIndex, async () => {
        const res = await fetch(`/api/diClientData/${period}`, {
          method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'revoke-sign-off', contIndex: row.contIndex }),
        });
        const j = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(j.error || `HTTP ${res.status}`);
      }, { revert: () => patchRow(row.contIndex, () => row), refetch: silentRefetch });
      toast.success(`${row.clientCode} sign-off taken back — you can edit it again.`);
    } catch (err) {
      toast.error(`Couldn't take back the sign-off: ${err.message}`, ERR_TOAST);
    }
  }

  async function adminUnmark(row, role) {
    if (!isAdmin) return;
    if (!window.confirm(`Unmark the ${role} sign-off for ${row.clientCode}? They will need to sign off again.`)) return;
    // The server clears the partner's sign-off along with the manager's (it rests on it), so show both gone.
    patchRow(row.contIndex, r => ({ ...r, reviews: { ...r.reviews, [role]: null, ...(role === 'manager' ? { partner: null } : {}) } }));
    try {
      await mutations.run(row.contIndex, () => postAdminUnmark(period, 'client-data', row.contIndex, role),
        { revert: () => patchRow(row.contIndex, () => row), refetch: silentRefetch });
      toast.success('Unmarked.');
    } catch (err) {
      toast.error(`Couldn't unmark ${row.clientCode}: ${err.message}`, ERR_TOAST);
    }
  }

  // ── edit ───────────────────────────────────────────────────────────────
  // Swap in the server's fresh rows. A row reassigned away from a non-admin
  // caller leaves their list.
  const mergeFresh = useCallback(freshRows => {
    if (!freshRows.length) return;
    const by = new Map(freshRows.map(f => [String(f.contIndex), f]));
    setData(d => {
      if (!d) return d;
      return { ...d, rows: d.rows
        // Edit responses don't carry billing or group info, so keep what we already have.
        .map(r => { const f = by.get(String(r.contIndex)); return f ? { ...f, billedTtm: r.billedTtm, group: r.group, partnerEdited: r.partnerEdited } : r; })
        .filter(r => d.adminView || r.myRoles.length > 0) };
    });
  }, [setData]);

  // Inline edit: save the one change straight from the row, optimistically. `optimistic` is the
  // row patch to show instantly; the edited property gets its "pending PE" dot right away — or loses it
  // right away when the edit puts it back to PE's value (nextPending).
  // Saves for a client are queued, so toggling several things quickly can't flash an earlier
  // server answer over a later edit: only the LAST save's response is merged back into the row.
  async function quickSave(row, changes, optimistic) {
    const key = String(row.contIndex);
    const dots = Object.keys(changes).filter(k => !DI_ONLY.has(k)).map(k => (k === 'managerEmail' ? 'manager' : k === 'partnerEmail' ? 'partner' : k));
    patchRow(row.contIndex, r => ({ ...r, ...optimistic, pending: nextPending(r, optimistic, dots) }));
    try {
      await mutations.run(key, async () => {
        const res = await fetch(`/api/diClientData/${period}`, {
          method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'save-attributes', contIndex: row.contIndex, changes }),
        });
        const j = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(j.error || `HTTP ${res.status}`);
        if (j.row && mutations.pending(key) <= 1) mergeFresh([j.row]);
      }, { revert: () => patchRow(row.contIndex, () => row), refetch: silentRefetch });
    } catch (err) {
      toast.error(`Couldn't save ${row.clientCode}: ${err.message}`, ERR_TOAST);
    }
  }

  // Mark No Longer Client: the same disengagement form + server process as Stale
  // Clients. The client's Partner (or an unimpersonated admin) marks it now: the row leaves this list
  // and every section. A Manager's mark FILES A REQUEST instead (outcome 'requested'): the row stays,
  // flagged "NLC requested", until the Partner confirms. Any related parties picked in the form go
  // through the same per-client rules.
  //
  // The viewer's standing is known from the row (the same test that words the modal), so the row is
  // patched at once — removed when the mark is direct, flagged when it is a request — and put back if
  // the server refuses; the modal stays open until the answer so its inline error keeps the answers.
  // The server's `outcome` has the last word (e.g. 'already_nlc' / 'already_requested').
  async function markNlc(row, disengagement) {
    setNlcSaving(true);
    setNlcError(null);
    const direct = adminView || row.myRoles.includes('partner');
    const same = r => String(r.contIndex) === String(row.contIndex);
    const at = rows.findIndex(same);
    // Take the row out of the list (and count it in the firm-wide NLC total) — only if it is still there.
    const dropRow = () => setData(d => (d && d.rows.some(same)
      ? { ...d, rows: d.rows.filter(r => !same(r)), summary: bumpNlc(d.summary, 1) } : d));
    // Put the row back (optionally transformed), un-counting it if it had been dropped.
    const putRow = transform => setData(d => {
      if (!d) return d;
      if (d.rows.some(same)) return transform ? { ...d, rows: d.rows.map(r => (same(r) ? transform(r) : r)) } : d;
      const list = d.rows.slice();
      list.splice(Math.min(at < 0 ? list.length : at, list.length), 0, transform ? transform(row) : row);
      return { ...d, rows: list, summary: bumpNlc(d.summary, -1) };
    });

    if (direct) dropRow();
    else patchRow(row.contIndex, r => ({ ...r, nlcRequest: r.nlcRequest || requestFlagFrom(null, email) }));

    let j;
    try {
      // Queued behind any save still in flight for this client.
      j = await mutations.run(row.contIndex, async () => {
        const res = await fetch(`/api/diClientData/${period}`, {
          method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'mark-nlc', contIndex: row.contIndex, disengagement }),
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
        return body;
      }, {
        revert: () => (direct ? putRow() : patchRow(row.contIndex, () => row)),
        refetch: silentRefetch,
      });
    } catch (err) {
      setNlcError(err.message);
      toast.error(`Couldn't mark ${row.clientCode} NLC: ${err.message}`, ERR_TOAST);
      setNlcSaving(false);
      return;
    }
    setNlcRow(null);
    setNlcSaving(false);
    const outcome = announceNlcOutcome(j, row.clientCode,
      `${row.clientCode} marked No Longer Client — it's now excluded from every section.`);
    if (nlcLeavesList(outcome)) {
      setSelected(s => { const n = new Set(s); n.delete(String(row.contIndex)); return n; });
      dropRow();              // already gone for a direct mark; a request that turned out moot leaves now
    } else {
      // A request: keep (or bring back) the row, flagged, with the server's request id for Withdraw.
      const flag = r => ({ ...r, nlcRequest: { ...(r.nlcRequest || {}), ...requestFlagFrom(j, email) } });
      putRow(flag);
    }
    await sweepRelated({
      disengagement, primaryContIndex: row.contIndex, primaryClientCode: row.clientCode, period,
      sourceSectionKey: 'client-data',
    });
    silentRefetch();        // refresh the NLC count and drop any related parties that were in this list
  }

  // The requester takes a pending request back: the badge goes at once; a failure restores it.
  async function withdrawNlc(row) {
    const req = row.nlcRequest;
    if (!req) return;
    patchRow(row.contIndex, r => ({ ...r, nlcRequest: null }));
    try {
      await withdrawNlcRequest(req.id);
      toast.success(`Request for ${row.clientCode} withdrawn.`);
    } catch (err) {
      patchRow(row.contIndex, r => ({ ...r, nlcRequest: req }));
      toast.error(`Couldn't withdraw the request for ${row.clientCode}: ${err.message}`, ERR_TOAST);
    }
  }

  function changeManager(row, email) {
    if (!email || email === row.managerEmail) return;
    const name = (employees.find(e => e.email === email) || {}).name || email;
    if (!window.confirm(`Reassign ${row.clientCode} to ${name} as Client Manager?`)) return;
    quickSave(row, { managerEmail: email }, { managerEmail: email, manager: name });
  }

  function changePartner(row, email) {
    if (!email || email === row.partnerEmail) return;
    const name = (employees.find(e => e.email === email) || {}).name || email;
    if (!window.confirm(`Reassign ${row.clientCode} to ${name} as Client Partner? It will move to their list and come off yours.`)) return;
    quickSave(row, { partnerEmail: email }, { partnerEmail: email, partner: name });
  }

  // Bulk sign-off: each selected client is signed as the viewer's own role on it (Partner when they are the
  // Partner — which also signs the Manager's, as a single sign-off does — else Manager). Optimistic like
  // signOff; the server judges each client, refused ones go back as they were and are listed in the banner.
  async function bulkSignOff(confirmed = false) {
    const targets = selectedRows.filter(canBulkSign);
    if (!targets.length) return;
    if (!confirmed) {
      // A Partner signing for clients whose Manager hasn't signed yet also signs the Manager's: warn first
      // (Continue re-enters here confirmed). Someone who is both Manager and Partner on a client isn't skipping anyone.
      const unsigned = targets.filter(r => signRole(r) === 'partner' && !r.reviews.manager && !r.myRoles.includes('manager')).length;
      if (unsigned) { setMgrWarn({ unsigned, total: targets.length }); return; }
    }
    setMgrWarn(null);
    const snapshots = new Map(targets.map(r => [String(r.contIndex), r]));
    const stamp = { by: 'You', at: new Date().toISOString() };
    for (const r of targets) {
      const role = signRole(r);
      patchRow(r.contIndex, cur => ({
        ...cur,
        reviews: { ...cur.reviews, [role]: stamp, ...(role === 'partner' && !cur.reviews.manager ? { manager: stamp } : {}) },
      }));
    }
    exitBulk();
    setSkipped(null);

    const request = fetch(`/api/diClientData/${period}`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'bulk-sign-off', contIndexes: targets.map(r => r.contIndex), role: activeRole || undefined }),
    }).then(async res => {
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
      return body;
    });
    targets.forEach(r => mutations.run(r.contIndex, () => request, {
      revert: () => patchRow(r.contIndex, () => snapshots.get(String(r.contIndex))),
      refetch: silentRefetch,
    }).catch(() => {}));
    try {
      const j = await request;
      const failed = j.failed || [];
      for (const f of failed) {
        const was = snapshots.get(String(f.contIndex));
        if (was) patchRow(f.contIndex, () => was);
      }
      mergeFresh(j.rows || []);
      setSkipped(failed.length ? failed : null);
      if (failed.length === 0) toast.success(`Signed off ${j.signed} client${j.signed === 1 ? '' : 's'}.`);
      else toast.warn(`Signed off ${j.signed} of ${j.requested}; ${failed.length} skipped.`, ERR_TOAST);
    } catch (err) {
      setBulkMode(true);
      setSelected(new Set(targets.map(r => String(r.contIndex))));
      toast.error(`Bulk sign-off failed: ${err.message}`, ERR_TOAST);
    }
  }

  // Bulk edit: one change-set for every selected client; the server decides per client. Optimistic:
  // every selected row shows the change at once (Manager / Partner reassignment only where the viewer
  // may make it — the server skips the rest) and the modal closes. Then the server's fresh rows
  // replace ours; the clients it refused go back to their old rows and are listed in the banner; a
  // failure of the whole request restores every row and the selection, with the error toasted.
  async function bulkSave(changes) {
    const targets = selectedRows;
    if (!targets.length) return;
    const keepSelection = new Set(selected);
    const snapshots = new Map(targets.map(r => [String(r.contIndex), r]));   // the rendered rows
    for (const r of targets) {
      const { patch, dots } = optimisticFor(changes, adminView || r.myRoles.includes('partner'), employees);
      patchRow(r.contIndex, cur => ({ ...cur, ...patch, pending: nextPending(cur, patch, dots) }));
    }
    exitBulk();
    setSkipped(null);

    const request = fetch(`/api/diClientData/${period}`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'bulk-save-attributes', contIndexes: targets.map(r => r.contIndex), changes }),
    }).then(async res => {
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
      return body;
    });
    // Register every selected client with the queue so a refetch landing mid-save can't revert them,
    // and they stay listed afterwards even if the edit stops them matching the filters. A whole-request
    // failure reverts each row through its own `revert`.
    targets.forEach(r => mutations.run(r.contIndex, () => request, {
      revert: () => patchRow(r.contIndex, () => snapshots.get(String(r.contIndex))),
      refetch: silentRefetch,
    }).catch(() => {}));
    try {
      const j = await request;
      const failed = j.failed || [];
      for (const f of failed) {
        const was = snapshots.get(String(f.contIndex));
        if (was) patchRow(f.contIndex, () => was);          // refused: show it as it was
      }
      mergeFresh(j.rows || []);
      setSkipped(failed.length ? failed : null);
      if (failed.length === 0) toast.success(`Updated ${j.updated} client${j.updated === 1 ? '' : 's'} — pending PE.`);
      else toast.warn(`Updated ${j.updated} of ${j.requested}; ${failed.length} skipped.`, ERR_TOAST);
    } catch (err) {
      setBulkMode(true);                                    // back to where they were, to retry
      setSelected(keepSelection);
      toast.error(`Bulk edit failed: ${err.message}`, ERR_TOAST);
    }
  }

  // ── filtering ──────────────────────────────────────────────────────────
  const offices  = useMemo(() => [...new Set(rows.map(r => r.office))].filter(Boolean).sort(), [rows]);
  const partners = useMemo(() => [...new Set(rows.map(r => r.partner))].filter(Boolean).sort(), [rows]);
  const managers = useMemo(() => [...new Set(rows.map(r => r.manager))].filter(Boolean).sort(), [rows]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    const tests = PROPS.filter(p => active.has(p.key)).map(p => p.test);
    const out = rows.filter(r => {
      if (pinned.has(String(r.contIndex))) return true;     // edited this session — stays until a filter changes
      if (s && !(`${r.clientCode} ${r.clientName} ${r.partner} ${r.manager} ${(r.group && r.group.sgName) || ''}`.toLowerCase().includes(s))) return false;
      if (groupMode === 'grouped'    && !groupDisplay(r.group)) return false;
      if (groupMode === 'standalone' &&  groupDisplay(r.group)) return false;
      if (groupOnly && (r.group || {})[groupOnly.kind + 'Code'] !== groupOnly.code) return false;
      if (office  && r.office  !== office)  return false;
      if (partner && r.partner !== partner) return false;
      if (manager && r.manager !== manager) return false;
      if (ferType === 'fam'      && !r.isFam)      return false;
      if (ferType === 'emp'      && !r.isEmp)      return false;
      if (ferType === 'referral' && !r.isReferral) return false;
      if (status === 'needs'      && !needsSignoff(r, activeRole)) return false;
      if (status === 'signed'     && needsSignoff(r, activeRole))  return false;
      if (status === 'pending-pe' && r.pending.length === 0) return false;
      if (status === AWAITING_PARTNER && !awaitsMyPartnerSignoff(r)) return false;
      return tests.every(t => t(r));
    });
    // Open rows first, completed rows last. On a client the viewer is Partner of, the manager-signed ones
    // (waiting on the partner) lead and the not-yet-manager-signed ones follow.
    return sortByTier(out, r => {
      if (!needsSignoff(r, activeRole)) return 2;
      return r.myRoles.includes('partner') && r.reviews.manager ? 0 : 1;
    });
  }, [rows, activeRole, search, office, partner, manager, ferType, status, active, groupMode, groupOnly, pinned]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage   = Math.min(page, totalPages);
  const paged      = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const anyFilter = !!(search || office || partner || manager || ferType || status || active.size || groupMode || groupOnly);
  const resetPage = () => setPage(1);
  const toggleProp = key => {
    setActive(prev => { const n = new Set(prev); if (n.has(key)) n.delete(key); else n.add(key); return n; });
    resetPage();
  };
  const clearAll = () => {
    setSearch(''); setOffice(''); setPartner(''); setManager(''); setFerType(''); setStatus(''); setActive(new Set());
    setGroupMode(''); setGroupOnly(null); setPage(1);
  };

  // ── progress (derived from rows so optimistic sign-offs show instantly) ──
  const progress = useMemo(() => {
    const total = rows.length;
    const done = rows.filter(r => !needsSignoff(r, activeRole)).length;
    return { total, done, pct: total ? Math.round((done / total) * 100) : 0 };
  }, [rows, activeRole]);

  // The property cards' counts, derived from rows too (like progress) so a toggle moves them at once —
  // the server's summary only changes on the next refetch. `rows` is every client the viewer can see,
  // the same population the server's summary counts.
  const propCounts = useMemo(
    () => Object.fromEntries(PROPS.map(p => [p.key, rows.filter(p.test).length])),
    [rows]);

  // May the viewer edit this client at all? Mirrors the server: a role on it (or
  // admin), and — for non-admins — not once all of their own roles have signed off.
  const canEditRow = r => !readOnly && (adminView || r.myRoles.some(role => !r.reviews[role]));
  // Inline controls are live only outside bulk mode (bulk mode is about selecting).
  const inlineOn = r => !bulkMode && canEditRow(r);
  const canEditManager = r => adminView || r.myRoles.includes('partner');
  // The role a bulk sign-off signs a client as: Partner if the viewer is its Partner, else Manager; null = not theirs.
  // With the role toggle on, it is always the viewed role.
  const signRole = r => (activeRole
    ? (r.myRoles.includes(activeRole) ? activeRole : null)
    : (r.myRoles.includes('partner') ? 'partner' : r.myRoles.includes('manager') ? 'manager' : null));
  const canBulkSign = r => { const role = signRole(r); return !!role && !r.reviews[role]; };
  const isPartnerOfAny = rows.some(r => r.myRoles.includes('partner'));

  // Selection is limited to what's currently visible, so a filter change can't
  // leave hidden clients silently selected.
  const selectableRows = filtered.filter(canEditRow);
  const selectedRows = bulkMode ? selectableRows.filter(r => selected.has(String(r.contIndex))) : [];
  const pageSelectable = paged.filter(canEditRow);
  const pageAllSelected = pageSelectable.length > 0 && pageSelectable.every(r => selected.has(String(r.contIndex)));
  const toggleOne = r => setSelected(prev => {
    const n = new Set(prev); const k = String(r.contIndex);
    if (n.has(k)) n.delete(k); else n.add(k);
    return n;
  });
  const togglePage = () => setSelected(prev => {
    const n = new Set(prev);
    pageSelectable.forEach(r => { if (pageAllSelected) n.delete(String(r.contIndex)); else n.add(String(r.contIndex)); });
    return n;
  });
  const selectAllMatching = () => setSelected(new Set(selectableRows.map(r => String(r.contIndex))));
  const exitBulk = () => { setBulkMode(false); setSelected(new Set()); setBulkOpen(false); };

  // The Manager and Partner lists differ, so a switch starts clean: no selection, no status / group
  // filter carried over (the "awaiting my sign-off" status only exists for a Partner).
  function changeRole(role) {
    if (role === activeRole) return;
    exitBulk();
    clearAll();
    setRoleChoice(role);
  }

  if (initialLoading) return <div className="rt-loading">Loading client data…</div>;
  if (error && !data)  return <div className="rt-error">Error: {error}</div>;
  if (!data)           return null;

  const summary = data.summary || {};
  const anyEditable = rows.some(canEditRow);

  // One sign-off toggle cell (Manager or Partner).
  const signCell = (row, role) => {
    const signed = row.reviews[role];
    const mine = row.myRoles.includes(role);
    // A manager can take their sign-off back until the partner has signed off or saved a change.
    const revocable = !isAdmin && canRevoke(row, role);
    const lockedWhy = !isAdmin && !readOnly && role === 'manager' && mine && signed && !revocable
      ? (row.reviews.partner ? 'The partner has signed off, so this can no longer be taken back.' : 'The partner has made changes, so this can no longer be taken back.')
      : (revocable ? 'Signed off — switch off to edit again (until the partner signs off or makes changes).' : undefined);
    return (
      <td className="cd-c" title={lockedWhy}>
        <ToggleSwitch
          checked={!!signed}
          disabled={signed ? !((isAdmin && !readOnly) || revocable) : (readOnly || !mine)}
          onChange={checked => {
            if (checked && !signed && mine) signOff(row, role);
            else if (!checked && signed) {
              if (isAdmin) adminUnmark(row, role);
              else if (revocable) revokeSignOff(row);
            }
          }}
        />
        {signed && signed.by && signed.by !== 'You' && <span className="cd-by" title={`Signed by ${signed.by}`}>✓</span>}
      </td>
    );
  };

  return (
    <div className="rt-page cd-page">
      {/* Top of the page, compact: the title and the progress card share the header row; the
          description and the PE-data timestamp share the next; then the filter chips and the search row. */}
      <div className="rt-header cd-top">
        <div className="rt-title-block">
          <h2 className="rt-title">Client Data</h2>
          {readOnly && <span className="rt-readonly-badge">Read-only</span>}
          <LastRefreshed at={lastRefreshedAt} freshness={freshness} onRefresh={silentRefetch} />
          {activeRole && <RoleToggle role={activeRole} onChange={changeRole} />}
        </div>
        {rows.length > 0 && (
          <div className="rt-progress-section cd-progress">
            <div className="rt-progress-header">
              <h3 className="rt-progress-title">{adminView ? 'All Clients' : 'Your Progress'}</h3>
              <span className="rt-progress-label-val">
                <span className="rt-progress-count">{progress.done} / {progress.total}</span>
                <span className="rt-progress-divider">—</span>
                {progress.pct}% Signed Off
              </span>
            </div>
            <div className="rt-progress-track">
              <div className="rt-progress-fill" style={{
                width: `${progress.pct}%`,
                background: progress.pct >= 100 ? '#1a7a4a' : progress.pct >= 50 ? '#e6a817' : '#c0392b',
              }} />
            </div>
          </div>
        )}
      </div>

      <div className="cd-meta">
        <p className="cd-sub">
          {adminView
            ? 'Every client with its Practice Engine attributes, Manager and Partner. As an admin you see all clients.'
            : 'Your clients — where you are the Client Manager or Partner. Review each client\'s attributes or bulk edit clients, then sign off.'}
          {' '}Edits are recorded here and marked <span className="cd-pend">●</span> pending PE.
        </p>
        {data.mirroredAt && (() => {
          const ageH = (Date.now() - new Date(data.mirroredAt).getTime()) / 3_600_000;
          const stale = ageH > 48;     // the PE gateway itself refuses to serve past 48h
          // The "data as of" timestamp is no longer shown; only the stale warning remains.
          return stale ? (
            <p className="cd-asof cd-asof--stale">
              Practice Engine data is more than 48 hours old; the PE sync may be failing.
            </p>
          ) : null;
        })()}
      </div>

      {allRows.length === 0 && !adminView && (
        <div className="cd-banner">You aren&apos;t the Client Manager or Partner on any clients in Practice Engine, so there&apos;s nothing to review here.</div>
      )}
      {rows.length === 0 && adminView && (
        <div className="cd-banner cd-banner--warn">
          No clients came back from the Practice Engine mirror. It may be empty or mid-sync — check the
          sync status before relying on this screen.
        </div>
      )}

      <div className="rt-summary cd-chips">
        <button type="button" className={`rt-stat cd-stat${active.size === 0 && !status ? ' cd-stat--on' : ''}`} onClick={clearAll} title="Clear all filters">
          <span className="rt-stat-val">{rows.length}</span>
          <span className="rt-stat-lbl">{adminView ? 'Total Clients' : 'Your Clients'}</span>
        </button>
        <button type="button" className={`rt-stat rt-stat--pending cd-stat${status === 'needs' ? ' cd-stat--on' : ''}`}
                onClick={() => { setStatus(s => (s === 'needs' ? '' : 'needs')); resetPage(); }}>
          <span className="rt-stat-val">{progress.total - progress.done}</span>
          <span className="rt-stat-lbl">Needs Sign-off</span>
        </button>
        {PROPS.map(p => (
          <button key={p.key} type="button"
                  className={`rt-stat cd-stat${active.has(p.key) ? ' cd-stat--on' : ''}`}
                  aria-pressed={active.has(p.key)} onClick={() => toggleProp(p.key)}>
            <span className="rt-stat-val">{propCounts[p.key]}</span>
            <span className="rt-stat-lbl">{p.label}</span>
          </button>
        ))}
      </div>

      <div className="rt-filters">
        <input className="rt-filter-input" placeholder="Search code, client, partner or manager…"
               value={search} onChange={e => { setSearch(e.target.value); resetPage(); }} />
        <select className="rt-filter-select" value={status} onChange={e => { setStatus(e.target.value); resetPage(); }}>
          {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          {isPartnerOfAny && <option value={AWAITING_PARTNER}>Manager signed — awaiting my sign-off</option>}
        </select>
        <select className="rt-filter-select" value={office} onChange={e => { setOffice(e.target.value); resetPage(); }}>
          <option value="">All Offices</option>
          {offices.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
        {adminView && (
          <>
            <select className="rt-filter-select" value={partner} onChange={e => { setPartner(e.target.value); resetPage(); }}>
              <option value="">All Partners</option>
              {partners.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
            <select className="rt-filter-select" value={manager} onChange={e => { setManager(e.target.value); resetPage(); }}>
              <option value="">All Managers</option>
              {managers.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </>
        )}
        <select className="rt-filter-select" value={ferType} onChange={e => { setFerType(e.target.value); resetPage(); }}>
          {FER_TYPES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        {data.groupsAvailable && (
          <select className="rt-filter-select" value={groupMode} onChange={e => { setGroupMode(e.target.value); resetPage(); }}>
            <option value="">Grouped or standalone</option>
            <option value="grouped">In a group</option>
            <option value="standalone">Standalone</option>
          </select>
        )}
        {groupOnly && (
          <span className="cd-chip" title="Only clients in this group are listed">
            {groupOnly.kind === 'rg' ? 'Relationship group' : 'Signer group'}: {groupOnly.label}{adminView ? '' : ' (your clients)'}
            <button type="button" aria-label="Remove group filter" onClick={() => { setGroupOnly(null); resetPage(); }}>×</button>
          </span>
        )}
        {anyFilter && <button type="button" className="rt-filter-clear" onClick={clearAll}>Clear</button>}
        {/* Bulk edit sits in the filter row, at its right edge. */}
        {!readOnly && anyEditable && !bulkMode && (
          <button type="button" className="cd-btn cd-btn--ghost cd-filters-bulk" onClick={() => setBulkMode(true)}>Bulk edit…</button>
        )}
      </div>

      {skipped && (
        <div className="cd-banner cd-banner--warn">
          <button type="button" className="cd-banner-x" aria-label="Dismiss" onClick={() => setSkipped(null)}>×</button>
          <strong>{skipped.length} client{skipped.length === 1 ? ' was' : 's were'} skipped in the bulk edit:</strong>
          <ul className="cd-skiplist">
            {Object.entries(skipped.reduce((acc, f) => { (acc[f.error] = acc[f.error] || []).push(f.clientCode || f.contIndex); return acc; }, {}))
              .map(([msg, codes]) => (
                <li key={msg}>{msg} — {codes.slice(0, 8).join(', ')}{codes.length > 8 ? ` and ${codes.length - 8} more` : ''}</li>
              ))}
          </ul>
        </div>
      )}

      {bulkMode && (
        <div className="cd-bulkbar">
          <strong>{selectedRows.length}</strong> of {selectableRows.length} editable client{selectableRows.length === 1 ? '' : 's'} selected
          {selectableRows.length > selectedRows.length && (
            <button type="button" className="cd-link" onClick={selectAllMatching}>
              Select all {selectableRows.length.toLocaleString()}{anyFilter ? ' matching filters' : ''}
            </button>
          )}
          {selectedRows.length > 0 && <button type="button" className="cd-link" onClick={() => setSelected(new Set())}>Clear selection</button>}
          {isPartnerOfAny && (
            <button type="button" className={`cd-btn cd-btn--ghost${status === AWAITING_PARTNER ? ' cd-btn--on' : ''}`}
                    aria-pressed={status === AWAITING_PARTNER}
                    title="Show only your clients the Manager has signed off that you haven't signed yet"
                    onClick={() => { setStatus(s => (s === AWAITING_PARTNER ? '' : AWAITING_PARTNER)); resetPage(); }}>
              Manager signed — awaiting my sign-off
            </button>
          )}
          <span className="cd-bulkbar-spacer" />
          <button type="button" className="cd-btn cd-btn--primary" disabled={selectedRows.length === 0} onClick={() => setBulkOpen(true)}>
            Edit selected…
          </button>
          {(() => {
            const signable = selectedRows.filter(canBulkSign);
            const roles = new Set(signable.map(signRole));
            const as = roles.size === 1 ? ` as ${[...roles][0] === 'partner' ? 'Partner' : 'Manager'}` : '';
            return (
              <button type="button" className="cd-btn cd-btn--primary" disabled={signable.length === 0} onClick={() => bulkSignOff()}
                      title="Sign off the selected clients as your own role on each (a Partner sign-off also signs the Manager's). Clients you've already signed are skipped.">
                Sign off {signable.length || ''} selected{as}
              </button>
            );
          })()}
          <button type="button" className="cd-btn cd-btn--ghost" onClick={exitBulk}>Cancel</button>
        </div>
      )}

      <div className="rt-table-wrap">
        <table className="rt-table">
          <thead>
            <tr>
              {bulkMode && (
                <th className="cd-c">
                  <input type="checkbox" className="cd-inline" aria-label="Select all on this page"
                         checked={pageAllSelected} disabled={pageSelectable.length === 0} onChange={togglePage} />
                </th>
              )}
              <th>Code</th>
              <th>Client</th>
              <th>Office</th>
              <th>Partner</th>
              <th>Manager</th>
              {data.groupsAvailable && <th title="Signer / Relationship Group — click to see who else is in it">Group</th>}
              <th title="Is Paper · Orphan (Standalone) · DNO (Do Not Offshore) · Post 4/15">Flags</th>
              <th>FAM / EMP / Referral</th>
              {data.billingAvailable && <th className="cd-num" title="Invoiced over the last 12 months (this month to date + the 11 before)">Billed (12 mo)</th>}
              <th className="cd-c">Mgr Sign-off</th>
              <th className="cd-c">Prtr Sign-off</th>
              <th className="cd-c" title="Mark this client No Longer Client">NLC</th>
            </tr>
          </thead>
          <tbody>
            {paged.map(r => {
              const complete = !!(r.reviews.manager && r.reviews.partner);
              const live = inlineOn(r);
              const isSel = bulkMode && selected.has(String(r.contIndex));
              const toggleBool = (prop, value) => quickSave(r, { [prop]: value }, { [prop]: value });
              const cls = [complete ? 'rt-row-reviewed' : '', isSel ? 'cd-row-sel' : ''].filter(Boolean).join(' ');
              return (
                <tr key={r.contIndex} className={cls}>
                  {bulkMode && (
                    <td className="cd-c">
                      <input type="checkbox" className="cd-inline" aria-label={`Select ${r.clientCode}`}
                             checked={isSel} disabled={!canEditRow(r)}
                             title={canEditRow(r) ? undefined : 'You can\'t edit this client (not your client, or you already signed off)'}
                             onChange={() => toggleOne(r)} />
                    </td>
                  )}
                  <td>{r.clientCode}</td>
                  <td>{r.clientName}</td>
                  <td>{r.office}</td>
                  <td title={r.partnerEmail || undefined}>
                    {live && canEditManager(r) ? (
                      <>
                        <select className="cd-cell-select" value={r.partnerEmail || ''} aria-label="Client Partner"
                                onChange={e => changePartner(r, e.target.value)}>
                          {withCurrent(employees, r.partnerEmail, r.partner).map(e => <option key={e.email} value={e.email}>{e.name}</option>)}
                        </select>
                        <Pend row={r} prop="partner" />
                      </>
                    ) : (
                      <>{r.partner || <span className="rt-cell-muted">—</span>}<Pend row={r} prop="partner" /></>
                    )}
                  </td>
                  <td title={r.managerEmail || undefined}>
                    {live && canEditManager(r) ? (
                      <>
                        <select className="cd-cell-select" value={r.managerEmail || ''} aria-label="Client Manager"
                                onChange={e => changeManager(r, e.target.value)}>
                          {withCurrent(employees, r.managerEmail, r.manager).map(e => <option key={e.email} value={e.email}>{e.name}</option>)}
                        </select>
                        <Pend row={r} prop="manager" />
                      </>
                    ) : (
                      <>{r.manager || <span className="rt-cell-muted">—</span>}<Pend row={r} prop="manager" /></>
                    )}
                  </td>
                  {data.groupsAvailable && (
                    <td>
                      {(() => {
                        const gd = groupDisplay(r.group);
                        return gd ? (
                          <button type="button" className="cd-group" onClick={() => setGroupRow(r)}
                                  title={`Signer group — ${gd.name} (${gd.size} clients). Click to see the members.`}>
                            <span className="cd-group-kind">{gd.kind}</span>
                            <span className="cd-group-name">{gd.name}</span>
                            <span className="cd-group-n">{gd.size}</span>
                          </button>
                        ) : <span className="rt-cell-muted" title="Standalone — not grouped with any other client">—</span>;
                      })()}
                    </td>
                  )}
                  <td><FlagsCell row={r} editable={live} onToggle={toggleBool} /></td>
                  <td><FerCell row={r} employees={employees} editable={live} onSave={(changes, optimistic) => quickSave(r, changes, optimistic)} /></td>
                  {data.billingAvailable && (
                    <td className="cd-num">
                      <button type="button" className="cd-billed" title="Show the last 12 months, month by month"
                              onClick={() => setBillingRow(r)}>
                        {r.billedTtm ? formatMoney(r.billedTtm) : '—'}
                      </button>
                    </td>
                  )}
                  {signCell(r, 'manager')}
                  {signCell(r, 'partner')}
                  <td className="cd-c">
                    {r.nlcRequest ? (
                      <NlcRequestedBadge request={r.nlcRequest} onWithdraw={() => withdrawNlc(r)} />
                    ) : live ? (
                      <ToggleSwitch variant="danger" checked={false}
                                    onChange={checked => { if (checked) { setNlcError(null); setNlcRow(r); } }} />
                    ) : <span className="rt-cell-muted" title={bulkMode ? undefined : 'You can\'t mark this client NLC (not your client, or you already signed off)'}>—</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {filtered.length === 0 && rows.length > 0 && <div className="rt-empty">No clients match these filters.</div>}
      </div>

      {filtered.length > PAGE_SIZE && (
        <div className="rt-pagination">
          <button disabled={safePage <= 1} onClick={() => setPage(1)}>«</button>
          <button disabled={safePage <= 1} onClick={() => setPage(p => p - 1)}>‹</button>
          <span className="rt-page-info">Page {safePage} of {totalPages} ({filtered.length.toLocaleString()} rows)</span>
          <button disabled={safePage >= totalPages} onClick={() => setPage(p => p + 1)}>›</button>
          <button disabled={safePage >= totalPages} onClick={() => setPage(totalPages)}>»</button>
        </div>
      )}

      {summary.nlcExcluded > 0 && (
        <p className="cd-foot">{summary.nlcExcluded.toLocaleString()} client{summary.nlcExcluded === 1 ? '' : 's'} marked No Longer Client are excluded.</p>
      )}

      {groupRow && (
        <ClientGroupModal
          period={period}
          row={groupRow}
          onClose={() => setGroupRow(null)}
        />
      )}

      {nlcRow && (
        <DisengagementModal
          client={{ code: nlcRow.clientCode, name: nlcRow.clientName }}
          {...nlcModalProps(adminView || nlcRow.myRoles.includes('partner') ? 'direct' : 'request')}
          submitting={nlcSaving}
          error={nlcError}
          onCancel={() => setNlcRow(null)}
          onConfirm={disengagement => markNlc(nlcRow, disengagement)}
        />
      )}

      {billingRow && <ClientBillingModal period={period} row={billingRow} onClose={() => setBillingRow(null)} />}

      {mgrWarn && (
        <ManagerUnsignedWarningModal
          unsigned={mgrWarn.unsigned}
          total={mgrWarn.total}
          cancelClass="cd-btn cd-btn--ghost"
          continueClass="cd-btn cd-btn--primary"
          onCancel={() => setMgrWarn(null)}
          onContinue={() => bulkSignOff(true)}
        />
      )}

      {bulkOpen && (
        <ClientDataBulkModal
          count={selectedRows.length}
          employees={employees}
          adminView={adminView}
          canEditManager={adminView || selectedRows.some(r => r.myRoles.includes('partner'))}
          onCancel={() => setBulkOpen(false)}
          onSave={bulkSave}
        />
      )}
    </div>
  );
}
